import moment from 'moment';
import Currency from '@app/model/_common/Currency';
import process from 'process';
import Price from '@app/model/market_state/Price';
import Month from '@app/model/market_state/Month';
import _replace from 'lodash/replace';
import _startCase from 'lodash/startCase';
import _lowerCase from 'lodash/lowerCase';
import _isEmpty from 'lodash/isEmpty';
import _truncate from 'lodash/truncate';
import { formatDateString } from '@util/CalendarDate.utilities';

class Utilities {
	public static classNames(...classes: (false | null | undefined | string)[]) {
		return classes.filter(Boolean).join(' ');
	}

	public static createRandomKey = (index: any = 0): number => {
		return Math.random() * 1000000000000000000 * (index + 1);
	};

	public static formatDouble = (num: number | undefined | null, minimumFractionDigits = 2, defaultValue = '0', alwaysShowFractionDigits = false): string => {
		if (!num) return defaultValue;
		const rounded = Math.round(num * Math.pow(10, minimumFractionDigits)) / Math.pow(10, minimumFractionDigits);
		const isWholeNumber = rounded % 1 === 0;
		return rounded.toLocaleString('en-US', { 
			minimumFractionDigits: isWholeNumber && !alwaysShowFractionDigits ? 0 : minimumFractionDigits,
			maximumFractionDigits: minimumFractionDigits 
		});
	};

	public static addPlusSign = (value: number): string | number => value > 0 ? `+${value}` : value;

	public static roundDouble(num: number | undefined | null, minimumFractionDigits = 2): number {
		if (!num) return 0;
		return Math.round(num * Math.pow(10, minimumFractionDigits)) / Math.pow(10, minimumFractionDigits);
	}

	public static formatAmount = (num: number, currency: Currency, minimumFractionDigits = 0, defaultValue = '0'): string => {
		return (num < 0 ? '-' : '') + Utilities.formatCurrency(currency) + Utilities.formatDouble(Math.abs(num), minimumFractionDigits, defaultValue);
	};

	public static formatCurrency = (currency: Currency): string => {
		// @ts-ignore
		return (!!currency ? Currency[currency] : '')
	}

		public static formatPrice = (price: Price, minimumFractionDigits = 0, defaultValue = '0'): string => {
		// @ts-ignore
		return Utilities.formatAmount(price.amount, price.currency, minimumFractionDigits, defaultValue);
	};

	public static now = (): string => {
		return moment.utc().format('yyyy-MM-DD HH:mm:ss');
	};

	public static formatDate = (date: Date, format = 'yyyy-MM-DD') => {
		return moment(date).format(format);
	};

	public static formatISODate = (date: Date) => {
		return Utilities.formatDate(date, 'yyyy-MM-DD');
	};
	public static formatISODateTime = (date: Date) => {
		return Utilities.formatDate(date, 'yyyy-MM-DD HH:mm:ss');
	};
	public static parseISODate = (date: Date) => {
		return moment(date, 'yyyy-MM-DD').toDate();
	};

	public static parseDateString = (date: string, format = 'yyyy-MM-DD') => {
		return moment(date, format).toDate();
	};

	public static parseDateToMillis = (date: string, format = 'yyyy-MM-DD') => {
		return moment(date, format).milliseconds();
	};

	public static formatDateString(date: string, format = 'DD/MM/yyyy', isLocal = true) {
		return formatDateString(date, format, isLocal);
	}

	public static createFormRequestParts = (form: any, attachments: File[] | undefined, formParamName = 'form', attachmentsParamName = 'attachments'): FormData => {
		const data = new FormData();
		data.append(formParamName, new Blob([JSON.stringify(form)], {
			type: 'application/json',
		}));
		Utilities.addAttachmentsToForm(data, attachments, attachmentsParamName);
		return data;
	};

	public static addAttachmentsToForm = (data: FormData, attachments: File[] | undefined, paramName = 'attachments') => {
		if (!!attachments) attachments.forEach(a => {
			if (a instanceof Blob) {
				const file = new File([a], a.name, { lastModified: new Date().getTime() });
				data.append(paramName, file);
			} else
				data.append(paramName, a);
		});
	};

	public static NVL = (value: boolean | undefined, property: string, def = '', prefix = '') => {
		return value ? prefix + property : def;
	};

	public static getInitials = (name: string) => {
		const allNames = name.trim().split(' ');
		const initials = allNames.reduce((acc, curr, index) => {
			if (index === 0 || index === allNames.length - 1) {
				acc = `${acc}${curr.charAt(0).toUpperCase()}`;
			}
			return acc;
		}, '');
		return initials;
	};

	public static listenForOutsideClicks = (listening: any, setListening: any, menuRef: any, setIsOpen: any, callback?: any) => {
		if (listening) return;
		if (!menuRef.current) return;
		setListening(true);

		const listener = (evt: Event) => {
			if (!!menuRef.current && menuRef.current.contains(evt.target)) return;
			setIsOpen(false);
			if (!!callback) callback();
		};

		document.addEventListener(`click`, listener);
		document.addEventListener(`touchstart`, listener);

	};

	public static getKeyByValue = <T>(enumeration: any, value: string): any => {
		const indexOf = Object.values(enumeration).indexOf(value as unknown as T);
		return Object.keys(enumeration)[indexOf];
	};

	public static isNumeric = (str: any) => {
		if (typeof str == 'number') return true; // we only process strings!
		if (typeof str != 'string') return false; // we only process strings!
		// @ts-ignore
		return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
			!isNaN(parseFloat(str)); // ...and ensure strings of whitespace fail
	};

	public static arrayRange = (start: number, stop: number, step: number = 1) =>
		Array.from(
			{ length: (stop - start) / step + 1 },
			(value, index) => start + index * step,
		);

	public static timeDifference = (date: string, format = 'yyyy-MM-DD HH:mm:ss') => {
		return Utilities.dateDifference(new Date().toISOString(), date, format);
	};

	public static dateDifference = (fromDate: string, toDate: string, format = 'yyyy-MM-DD HH:mm:ss') => {
		const from = moment.utc(fromDate);
		const to = moment.utc(toDate, format);
		const days = from.diff(to, 'days');
		if (days === 0) {
			const hours = from.diff(to, 'hours');
			if (hours === 0) {
				const minutes = from.diff(to, 'minutes');
				if (minutes === 0) {
					const seconds = from.diff(to, 'seconds');
					return seconds + (seconds === 1 ? ' second' : ' seconds');
				} else return minutes + (minutes === 1 ? ' minute' : ' minutes');
			} else return hours + (hours === 1 ? ' hour' : ' hours');

		} else return days + (days > 1 ? ' days' : ' day');
	};

	public static daysDifference = (fromDate: string, toDate: string, format = 'yyyy-MM-DD HH:mm:ss') => {
		const from = moment(fromDate, format).startOf('day');
		const to = moment(toDate, format).startOf('day');
		return from.diff(to, 'days');
	};

	public static daysDifferenceFromNow = (toDate: string, format = 'yyyy-MM-DD HH:mm:ss') => {
		const from = moment().startOf('day');
		const to = moment(toDate, format).startOf('day');
		return from.diff(to, 'days');
	};

	public static weekOfMonth = (date: string) => {
		const dayNumber = moment(date).format('D');
		return Math.ceil(parseInt(dayNumber, 10) / 7);
	};

	public static detectChromeExtension = () => {
		return new Promise<boolean>((resolve) => {
			fetch('chrome-extension://' + process.env.WEB_EXTENSION_APP_ID + '/assets/img/logo-bell-accent.svg')
				.then(() => {
					resolve(true);
				})
				.catch(() => {
					resolve(false);
				});
		});
	};

	public static snakeCaseToWords = (str: string) => {
		return _startCase(_lowerCase(_replace(str, '_', ' ')));
	};

	public static getMonthOrdinal = (month: Month): number => {
		return Object.keys(Month).indexOf(month);
	};

	public static getMonthByIndex = (idx: number): any => {
		return Object.keys(Month)[idx];
	};

	public static hasDuplicates = (array: any[]) => {
		return (new Set(array)).size !== array.length;
	};

	public static findRoomMappingName = (type: any, mappings: any[]) => {
		if (_isEmpty(mappings)) return Utilities.snakeCaseToWords(type);
		else {
			const mapping = mappings!.find(m => m.type === type);
			return !!mapping ? Utilities.snakeCaseToWords(mapping.name) : Utilities.snakeCaseToWords(type);
		}
	};

	static weeksInMonth(year: number, month: Month) {
		const startOfMonth = moment(new Date(year, this.getMonthOrdinal(month), 1));
		const endOfMonth = moment(new Date(year, this.getMonthOrdinal(month), 1)).endOf('month');
		return endOfMonth.week() - startOfMonth.week();
	}

	static trim(text: string, numOfChars: number) {
		return _truncate(text, {length: numOfChars, separator: ''});
	}

	static menuItemClasses(focus?: boolean) {
		return Utilities.classNames(
			focus ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
			'block px-4 py-2 text-xs relative cursor-pointer'
		)
	}
}

export default Utilities;
